'use client';

import { createClient } from '@/lib/supabase/client';

function isSchemaError(error: any): boolean {
  if (!error) return false;
  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42') return true;
    if (errorClass === '08') return true;
  }
  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    return schemaErrorPatterns.some((pattern) => pattern.test(error.message));
  }
  return false;
}

export interface RetailerApplication {
  id?: string;
  businessName: string;
  contactPerson: string;
  email: string;
  phone: string;
  businessType: string;
  businessAddress: string;
  currentDeliveryVolume: string;
  message?: string;
  status?: 'pending' | 'approved' | 'rejected';
  adminNotes?: string;
  reviewedBy?: string;
  reviewedAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

export const retailerApplicationService = {
  async submitApplication(application: Omit<RetailerApplication, 'id' | 'status' | 'adminNotes' | 'reviewedBy' | 'reviewedAt' | 'createdAt' | 'updatedAt'>) {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('retailer_applications')
        .insert({
          business_name: application.businessName,
          contact_person: application.contactPerson,
          email: application.email,
          phone: application.phone,
          business_type: application.businessType,
          business_address: application.businessAddress,
          current_delivery_volume: application.currentDeliveryVolume,
          message: application.message || null,
          status: 'pending',
        })
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Submit application error:', error.message);
          return null;
        }
      }

      return {
        id: data.id,
        businessName: data.business_name,
        email: data.email,
        status: data.status,
        createdAt: data.created_at,
      };
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async getAllApplications(): Promise<RetailerApplication[]> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const { data, error } = await supabase
        .from('retailer_applications')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Get applications error:', error.message);
          return [];
        }
      }

      return data?.map((row) => ({
        id: row.id,
        businessName: row.business_name,
        contactPerson: row.contact_person,
        email: row.email,
        phone: row.phone,
        businessType: row.business_type,
        businessAddress: row.business_address,
        currentDeliveryVolume: row.current_delivery_volume,
        message: row.message,
        status: row.status,
        adminNotes: row.admin_notes,
        reviewedBy: row.reviewed_by,
        reviewedAt: row.reviewed_at,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
      })) || [];
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async updateApplication(id: string, updates: { status?: string; adminNotes?: string; reviewedBy?: string }) {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const updateData: any = {};
      if (updates.status) updateData.status = updates.status;
      if (updates.adminNotes !== undefined) updateData.admin_notes = updates.adminNotes;
      if (updates.reviewedBy) {
        updateData.reviewed_by = updates.reviewedBy;
        updateData.reviewed_at = new Date().toISOString();
      }

      const { data, error } = await supabase
        .from('retailer_applications')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Update application error:', error.message);
          return null;
        }
      }

      return data;
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async deleteApplication(id: string) {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const { error } = await supabase
        .from('retailer_applications')
        .delete()
        .eq('id', id);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Delete application error:', error.message);
          return false;
        }
      }

      return true;
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async bulkUpdateStatus(ids: string[], status: string) {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const { error } = await supabase
        .from('retailer_applications')
        .update({ status })
        .in('id', ids);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Bulk update error:', error.message);
          return false;
        }
      }

      return true;
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async bulkDelete(ids: string[]) {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const { error } = await supabase
        .from('retailer_applications')
        .delete()
        .in('id', ids);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Bulk delete error:', error.message);
          return false;
        }
      }

      return true;
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },
};
